/**
 * HeaderSearch — the design header's real search control (Task 6, spec §4.1).
 *
 * The design draws a non-functional `<span>`; this is a genuine
 * `<form role="search">` that submits to the SAME destination and query
 * param the existing catalogue search uses (`SearchResultsPage`'s own
 * `PARAM_Q = "q"`), trimmed and capped at `SEARCH_Q_MAX_LEN` — the backend
 * 422s a `q` over that length. An empty submit is a no-op: the backend also
 * 422s an empty `q` in search mode, so nothing is ever sent.
 *
 * Deliberately NOT the existing `HeaderSearchBar` (recent-search dropdown,
 * 300ms debounce, mobile fullscreen overlay) — the design's field is a
 * single fixed-width input with none of that chrome, and layering the old
 * bar's behaviour underneath it would just be a second, divergent search
 * experience. The value is left in the field after submit (matches
 * `HeaderSearchBar`'s own behaviour: it keeps showing what was searched,
 * never clears itself out from under the user).
 *
 * The component owns its draft in local state and never reads the URL, so a
 * route change elsewhere does not remount or reset it — `DesktopHeader`
 * lives outside the routed `<Outlet>` region (mounted once by `AppShell`),
 * so this input's value and focus both survive navigation for free: nothing
 * here unmounts when the route underneath it changes.
 */
import { useState, type FormEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { SEARCH_Q_MAX_LEN } from "@/features/marketplace/useMarketplaceSearch";

export function HeaderSearch(): ReactElement {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [draft, setDraft] = useState<string>("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const query = draft.trim().slice(0, SEARCH_Q_MAX_LEN);
    if (query.length === 0) return; // empty submit is a no-op (backend 422s it)
    navigate(`/marketplace/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <form
      role="search"
      aria-label={t("header.search.formLabel")}
      onSubmit={handleSubmit}
      className="w-[280px] shrink-0"
    >
      <input
        type="search"
        inputMode="search"
        enterKeyHint="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        maxLength={SEARCH_Q_MAX_LEN}
        placeholder={t("header.search.placeholder")}
        aria-label={t("header.search.inputLabel")}
        data-testid="header-search"
        /* The placeholder is TEXT, so `--gd-ink-3` (5.32:1 on --gd-surface),
           per design-world.css's own rule: `--gd-ink-4` is a hairline / dot
           colour and measured 3.01:1 here (C5, ui-review.md §4.7). */
        className="w-full rounded-full placeholder:text-[color:var(--gd-ink-3)] focus:outline-none [&::-webkit-search-cancel-button]:appearance-none"
        style={{
          border: "1px solid var(--gd-rule-3)",
          backgroundColor: "var(--gd-surface)",
          padding: "9px 18px",
          fontFamily: "var(--gd-text)",
          fontSize: "13.5px",
          color: "var(--gd-ink-body)",
        }}
      />
    </form>
  );
}

export default HeaderSearch;
